import { ConvertGenConfigToCreativeConfig, CreativeServerGenConfig } from "../Creative/CreativeServer";
import { DailyActivity, GetInstantActivity, InstantActivity } from "../Schedule/ScheduleActivity";
import { DistributeArrayRandomly, GetBandedRandomArray, GetIntelligentRandomArray, GetRandomNumberNotInArray } from "../Utility/ArrayUtilities";
import { ContentContextData } from "./ContentContext";
import { ActivityBlockConfigData, ChatConfigData, CloneForQuizClient, CreativeConfigData, DefaultActivityBlockConfigData, DefaultPostConfigData, GenerateAffectReactPairComment, GenerateAffectReactPairPost, MessageConfigData, PartyConfigData, PostConfigData, PostElementConfigData,  ProjectVoting, QuizConfigData, VotesConfigData } from "./PartyConfigData";

export type ContentPackageConfigData = {
    startTime: number;
    endTime: number;
    main: ActivityBlockConfigData;
    chat?: ChatConfigData;
};


export const DefaultContentPackageConfigData = (): ContentPackageConfigData => {
    return {
        startTime: 0,
        endTime: 0,
        main: DefaultActivityBlockConfigData()
    }
}

export type ContentEnvironment = {
    startTime: number;
    daily: DailyActivity;
    nextDailyActivity : DailyActivity;
    creativeServerConfigs: { [key: string]: CreativeServerGenConfig };
    quizConfigs: { [key: string]: QuizConfigData };
}

export const CreateDevEndlessContentPackage = (time: number) => {
    const post = DefaultPostConfigData();

    const content: ContentPackageConfigData = {
        startTime: time,
        endTime: time + 1000000,
        main: {
            startTime: time,
            endTime: time + 1000000,
            activityType: 'post',
            //chatSource: 'integrated',
            mediaPosition: 'top',
            forceOptionalEnd: false,
            config: post
        }
    }
    return content;
}

export const ExtractContentPackages = (party: PartyConfigData, environment: ContentEnvironment): { content: ContentPackageConfigData, activity: InstantActivity }[] => {
    const ret: { content: ContentPackageConfigData, activity: InstantActivity }[] = [];
    party.main.forEach((act, index) => {
        const instantActivity = GetInstantActivity(environment.daily, environment.nextDailyActivity, act.startTime + environment.startTime);
        const activity = CloneActivityPackage(act, environment, instantActivity.national);
        const chat = activity.chatSourceId === undefined ? undefined : CloneChatAlt(party.chats[activity.chatSourceId], act.startTime, act.endTime, 3.5, environment.startTime, instantActivity.national);

        const content: ContentPackageConfigData = {
            startTime: act.startTime + environment.startTime,
            endTime: act.endTime + environment.startTime,
            main: activity,
            chat: chat
        }
        ret.push({ content: content, activity: instantActivity });
    });
    return ret;
}

export const CloneActivityPackage = (activity: ActivityBlockConfigData, environment: ContentEnvironment, online: number): ActivityBlockConfigData => {
    const absStartTime = environment.startTime;
    var chatId = activity.chatSourceId;
    const config = (() => {
        if (activity.activityType === 'post') {
            const post = ClonePost(activity.config as PostConfigData, absStartTime, online, activity.endTime - activity.startTime);
            if (post.chatId !== undefined)
                chatId = post.chatId;
            return post;
        }
        else if (activity.activityType === 'chat') {
            const chat = CloneChatAlt(activity.config as ChatConfigData, activity.startTime, activity.endTime, 3.5, absStartTime, online);
            return chat;
        }
        else if (activity.activityType === 'creative') {
            const gen = activity.config as CreativeServerGenConfig;
            const creative = ConvertGenConfigToCreativeConfig(gen);
            environment.creativeServerConfigs[gen.itemId] = gen;
            return creative;
        }
        else if (activity.activityType === 'quiz') {
            const quiz = activity.config as QuizConfigData;
            const cquiz = CloneForQuizClient(quiz);
            environment.quizConfigs[quiz.itemId] = quiz;
            return cquiz;
        }
        else {
            return activity.config;
        }
    })();


    const ret: ActivityBlockConfigData = {
        startTime: absStartTime + activity.startTime,
        endTime: absStartTime + activity.endTime,
        activityType: activity.activityType,
        //chatSource: activity.chatSource,
        chatSourceId: chatId,
        mediaPosition: activity.mediaPosition,
        //chatPosition: activity.chatPosition,
        forceOptionalEnd: activity.forceOptionalEnd,
        config: config,
    }

    return ret;
}

const ClonePost = (post: PostConfigData, absStartTime: number, online: number, timeBasis : number): PostConfigData => {

    const elements = Array<PostElementConfigData>();
    if(post.elements === undefined)
    {
        console.log(JSON.stringify(post));
    }
    post.elements.forEach((e) => {
        const element: PostElementConfigData = {
            itemId: e.itemId,
            chatId: e.chatId,
            title: e.title,
            layout: e.layout,
            voting: e.voting === undefined ? undefined : CloneVotingAlt(e.voting, true, absStartTime, online , 1,Math.floor((e.voting.popularity ?? 1.0) * 10000), timeBasis), 
            primary: {
                text: e.primary.text,
                imageSrc: e.primary.imageSrc,
                imageClick: e.primary.imageClick,
                buttonText: e.primary.buttonText,
                buttonClick: e.primary.buttonClick
            },
            secondary: e.secondary === undefined ? undefined : {
                text: e.secondary.text,
                imageSrc: e.secondary.imageSrc,
                imageClick: e.secondary.imageClick,
                buttonText: e.secondary.buttonText,
                buttonClick: e.secondary.buttonClick
            }
        }
        elements.push(element);
    })

    if(post.voting === undefined){
        post.voting = {
            itemId : post.itemId + '_voting',
            expression : 'inspiring',
            impressions : 0,
            likes : 0,
            comments : 0,
            created : 0,
            updated : 0
        }

    }

    const ret: PostConfigData = {
        itemId: post.itemId,
        title: post.title,
        author: post.author,
        layout: post.layout,
        rotate: post.rotate,
        messageDisplay: post.messageDisplay,
        elementDisplayTime: post.elementDisplayTime,
        messageMixRatio: post.messageMixRatio,
        voting: CloneVotingAlt(post.voting, true, absStartTime, online, 1,10000, timeBasis), 
        chatId: post.itemId.replace('mn', 'ch'),
        elements: elements
    }
    return ret;
}

const CloneVotingAlt = (votes: VotesConfigData, isPost : boolean, absStartTime: number, impressions: number, optionality : number, popularity : number, timeBasis : number): VotesConfigData => {

    const [affect, react] = isPost ? GenerateAffectReactPairPost() : GenerateAffectReactPairComment(); 
        
    const ret: VotesConfigData = {
        itemId: votes.itemId,
        impressions: 0,
        likes: 0,
        comments: 0,
        expression: votes.expression,
        created: absStartTime + votes.created,
        updated: -1,
        popularity: popularity,
        affect: affect,
        react: react
    }
    ProjectVoting(ret, absStartTime + votes.created, impressions, optionality,timeBasis);

    return ret;
}

/*const CloneVoting = (votes: VotesConfigData, absStartTime: number, online: number): VotesConfigData => {


    const ret: VotesConfigData = {
        itemId: votes.itemId,
        impressions: 0,
        likes: 0,
        comments: 0,
        expression: votes.expression,
        created: Math.round(absStartTime + votes.created),
        updated: Math.round(absStartTime + votes.created),
        popularity: (votes.popularity ?? 0.1) * online * 2.0 * 11.2,
        affect: votes.affect,
        react: votes.react
    }
    ProjectVoting(ret, absStartTime + votes.updated);

    if (ret.popularity === undefined) {
        console.log('vote popularity has been blanked');
    }

    return ret;
}*/





export const CloneChatAlt = (chat: ChatConfigData, startDisplay: number, endDisplay: number, messageRate: number, absStartTime: number, online: number): ChatConfigData => {
    const numMessages = Math.ceil((endDisplay - startDisplay) / 1000 / messageRate) + 6; // This means that 6 more messages are added that theoretically needed, but remembering 3 are needed to fill initial screen
    
    const optionality = Math.floor(online * 0.5);
   // console.log('Attempting to select ' + numMessages + ' for chat ' + chat.itemId);
    const timeIncrement = 1000 * messageRate;
     const timeBasis = endDisplay - startDisplay;

    const messageSource = chat.messages.slice();

    const messages = Array<MessageConfigData>();
    var time = startDisplay;
    const timeWindow = (numMessages + 1) * timeIncrement;
    var previousMesageCount = 0;
    for (let i = 0; i < numMessages; i++) {
        const available = Array<number>();
        messageSource.forEach((m, index) => {
            const ms = m.displayStart ?? startDisplay;
            const me = m.displayEnd ?? (startDisplay+ timeWindow);
            const endCount = Math.min(Math.ceil((me - time + 6000)/1000),3); // This 6000 is to braoden the display times so there is more choice of messages, the 3 means that a message appears up to 3 times in available tripling its probability of being seletced
            if (time >= ms && endCount > 0)
                for(let i = 0; i < endCount; i++)
                    available.push(index);
        });
        

        if (available.length > 0) {
            const index = Math.floor(Math.random() * available.length);
            const m1 = messageSource[available[index]];
            messageSource.splice(available[index], 1);

            //const m2 = CloneMessage(m1, absStartTime, online);
            previousMesageCount = messages.length;
            messages.push(m1)//(m2);
            //if(messages.length === i){
            if(i >= 3 && time < endDisplay) // We should only increment the time after 3 messages and if we aren't past the end of the end of display
                time += timeIncrement;
               // console.log('messages.length = ' + messages.length + ' i ' + i + ' num ' + numMessages );
            //}
            //else
           // {
             //   i--;
                //console.log('Repeating time cycle ' + time + 'available ' + available.length); 
           // }
        }
        else if(i >= 3 && time < endDisplay){
            time += timeIncrement;
            //console.log('No messages available, time' + time + ' i:' + i)
        }
    }
    //console.log('Selected ' + messages.length + ' for chat ' + chat.itemId);

    //const popularities = Array<number>();
    //const messageIndices = Array<number>();
    const messageTimings = Array<number>();
    for(let i = 0; i < messages.length; i++) {
        //popularities.push(-1);
        //messageIndices.push(i);
        const display = startDisplay + i * timeIncrement;
        const time = Math.round(display - timeBasis * (0.35 + 0.35 * Math.random())); // we are baiscally initialising the voting on the message between 35% and 70% of the time basis earlier than its display
        messageTimings.push(time);
    }

    /*
    var nProcess = 0;
    const quarterMessages = Math.floor(messages.length * 0.25);
    const halfMessages = Math.floor(messages.length * 0.5);
    
    const o1p = Math.max(Math.floor(optionality * 0.01), quarterMessages);
    const pop1 = GetIntelligentRandomArray(o1p, quarterMessages, 1);
    DistributeArrayRandomly(popularities, pop1, messageIndices);

    const o2p = Math.max(Math.floor(optionality * 0.05) - o1p, quarterMessages);
    const pop2 = GetIntelligentRandomArray(o2p, quarterMessages, o1p+1);
    DistributeArrayRandomly(popularities, pop2, messageIndices);

    const o3p = Math.max(Math.floor(optionality * 0.2) - o2p, quarterMessages);
    const pop3 = GetIntelligentRandomArray(o3p, quarterMessages, o1p + o2p + 1);
    DistributeArrayRandomly(popularities, pop3, messageIndices);

    /*
    //Adds 25% of the messages in the top 1% of popularity
    for(;nProcess < messages.length * 0.25; nProcess++) {
        const mindex = Math.floor(Math.random() * messageIndices.length);
        const index = messageIndices[mindex];
        messageIndices.splice(mindex,1);

        const rand = GetRandomNumberNotInArray(1,o1p, popularities);
        popularities[index] = rand;
    }

    //Adds 25% of the messages in the range 1-5% popularity
    for(;nProcess < messages.length * 0.5; nProcess++) {
        const mindex = Math.floor(Math.random() * messageIndices.length);
        const index = messageIndices[mindex];
        messageIndices.splice(mindex,1);

        const rand = GetRandomNumberNotInArray(o1p, o2p, popularities);
        popularities[index] = rand;
    }

    //Adds 25% of the messages in the range 5-20% popularity
    for(;nProcess < messages.length * 0.75; nProcess++) {
        const mindex = Math.floor(Math.random() * messageIndices.length);
        const index = messageIndices[mindex];
        messageIndices.splice(mindex,1);

        const rand = GetRandomNumberNotInArray(o2p, o3p, popularities);
        popularities[index] = rand;
    }

    //Adds 25% of the messages in the 20%+ range of popularity

    for(let i = 0; i < popularities.length; i++) {
        if(popularities[i] < 1)
        {
            const rand = GetRandomNumberNotInArray(o1p + o2p + o3p + 1, optionality + 1, popularities);
            popularities[i] = rand;
        }
    }*/

    const popularities = GetBandedRandomArray([Math.floor(optionality * 0.01),Math.floor(optionality * 0.05), Math.floor(optionality * 0.15), optionality ],
                                              [Math.floor(messages.length * 0.25), Math.floor(messages.length * 0.5), Math.floor(messages.length * 0.75), messages.length ],
                                              1);

    const processedMessages = Array<MessageConfigData>();

    for(let i = 0; i < messages.length; i++) {

        const message = messages[i];
        message.voting.created = messageTimings[i];

        const pMessage: MessageConfigData = {
        itemId: message.itemId,
        userId: message.userId,
        userColor: message.userColor,
        userIcon: message.userIcon,
        nickname: message.nickname,
        bodyText: message.bodyText,
        image: message.image,
        replies: [], 
        voting: CloneVotingAlt(message.voting,false, absStartTime, online * numMessages, optionality, popularities[i], timeBasis)
    };

    processedMessages.push(pMessage);   

    }

    const threads = chat.threads.map((thread) => {
        return CloneChatAlt(thread, startDisplay, endDisplay, messageRate, absStartTime, online);
    })

    const ret: ChatConfigData = {
        itemId: chat.itemId,
        chatType: chat.chatType,
        startTime: absStartTime + chat.startTime,
        endTime: absStartTime + chat.endTime,
        threads: threads,
        messages: processedMessages
    }
    return ret;

}

/*export const CloneChat = (chat: ChatConfigData, startDisplay: number, endDisplay: number, messageRate: number, absStartTime: number, online: number): ChatConfigData => {
    const numMessages = Math.ceil((endDisplay - startDisplay) / 1000 / messageRate) + 10;
   // console.log('Attempting to select ' + numMessages + ' for chat ' + chat.itemId);
    const timeIncrement = 1000 * messageRate;
   

    const messageSource = chat.messages.slice();
    const messages = Array<MessageConfigData>();
    var time = startDisplay;
    const timeWindow = (numMessages + 1) * timeIncrement;
    var previousMesageCount = 0;
    for (let i = 0; i < numMessages; i++) {
        const available = Array<number>();
        messageSource.forEach((m, index) => {
            const ms = m.displayStart ?? startDisplay;
            const me = m.displayEnd ?? (startDisplay+ timeWindow);
            const endCount = Math.min(Math.round((me - time + 15000)/3000 + 0.5),6) *0.2;
            if (time >= ms && endCount > 0)
                for(let i = 0; i < endCount; i++)
                    available.push(index);
        });
        

        if (available.length > 0) {
            const index = Math.floor(Math.random() * available.length);
            const m1 = messageSource[available[index]];
            messageSource.splice(available[index], 1);

            const m2 = CloneMessage(m1, absStartTime, online);
            previousMesageCount = messages.length;
            messages.push(m2);
            if(messages.length === i){
                time += timeIncrement;
               // console.log('messages.length = ' + messages.length + ' i ' + i + ' num ' + numMessages );
            }
            else
            {
                i--;
                //console.log('Repeating time cycle ' + time + 'available ' + available.length); 
            }
        }
        else {
            time += timeIncrement;
            //console.log('No messages available, time' + time + ' i:' + i)
        }
    }
    //console.log('Selected ' + messages.length + ' for chat ' + chat.itemId);

    const threads = chat.threads.map((thread) => {
        return CloneChat(thread, startDisplay, endDisplay, messageRate, absStartTime, online);
    })

    const ret: ChatConfigData = {
        itemId: chat.itemId,
        chatType: chat.chatType,
        startTime: absStartTime + chat.startTime,
        endTime: absStartTime + chat.endTime,
        threads: threads,
        messages: messages
    }
    return ret;

}*/



/*const CloneMessage = (message: MessageConfigData, absStartTime: number, online: number): MessageConfigData => {

    const replies = message.replies.map((reply) => {
        const nr = CloneMessage(reply, absStartTime, online);
        return nr;
    })

    const ret: MessageConfigData = {
        itemId: message.itemId,
        userId: message.userId,
        userColor: message.userColor,
        userIcon: message.userIcon,
        nickname: message.nickname,
        bodyText: message.bodyText,
        image: message.image,
        replies: replies,
        voting: CloneVoting(message.voting, absStartTime, online)
    };
    return ret;
}*/